import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiX, FiSend, FiUser } from 'react-icons/fi';
import { FaUserCircle } from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import { openaiService } from '../services/openaiService';
import { emailService } from '../services/emailService';
import { getAIBrain } from '../data/brain.js';

// Inactivity timeout: 5 minutes (ms)
const INACTIVITY_MS = 5 * 60 * 1000;

const ChatInterface = ({ onClose, isEmbed = false }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Thank you for contacting Rasa Sayang Asian Kitchen. How may I help you today?",
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // 🔒 Unique session ID — one email per conversation
  const [mountId] = useState(() => 'chat_session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));

  const messagesEndRef = useRef(null);
  const messagesRef = useRef(messages);
  const inactivityTimer = useRef(null);

  // 🔄 Keep ref in sync with state
  useEffect(() => {
    messagesRef.current = messages;
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // 📧 SEND SUMMARY — one email per session, full transcript
  const sendConversationSummary = async () => {
    const currentMsgs = messagesRef.current;

    // Skip if only the welcome message (no real conversation)
    if (currentMsgs.length <= 1) return;

    // Lock — prevent duplicate sends across all triggers
    if (sessionStorage.getItem(mountId)) {
      console.log('⚠️ Already sent for this session. Skipping.');
      return;
    }
    sessionStorage.setItem(mountId, 'true');

    const fullText = currentMsgs.map(m => m.text).join(' ');
    const isRes = /book|reserve|reservation|table for/i.test(fullText);

    const transcript = currentMsgs.map(m => {
      const timeVal = m.timestamp instanceof Date ? m.timestamp : new Date(m.timestamp);
      const timeString = timeVal.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const role = m.sender === 'user' ? '👤 CUSTOMER' : '🤖 AI';
      return `[${timeString}] ${role}: ${m.text}`;
    }).join('\n\n');

    const summaryBody = `
📝 CHAT CONVERSATION SUMMARY
---------------------------
${transcript}
---------------------------
End of Transcript
`.trim();

    try {
      await emailService.sendChatNotification({
        message: summaryBody,
        from_name: 'Chat Visitor',
        subject: isRes ? '📅 New Reservation (Chat Summary)' : '💬 Chat Conversation Summary',
      });
      console.log('✅ Summary Email Sent.');
    } catch (error) {
      console.error('❌ Summary Email Failed:', error);
    }
  };

  // ⏱️ Reset inactivity timer on every new message
  const resetInactivityTimer = () => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      console.log('⏱️ Inactivity timeout — sending summary.');
      sendConversationSummary();
    }, INACTIVITY_MS);
  };

  // 🚪 Setup all triggers on mount
  useEffect(() => {
    // Trigger 1: Tab hidden or closed (catches browser tab close / navigate away)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendConversationSummary();
      }
    };

    // Trigger 2: beforeunload (backup for direct browser close)
    const handleBeforeUnload = () => {
      sendConversationSummary();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Trigger 3: Normal close (X button)
      sendConversationSummary();
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMsgText = inputMessage.trim();
    const userMessage = {
      id: Date.now(),
      text: userMsgText,
      sender: 'user',
      timestamp: new Date(),
    };

    const newHistory = [...messages, userMessage];
    setMessages(newHistory);
    setInputMessage('');
    setIsLoading(true);
    resetInactivityTimer();

    // Create empty bot message placeholder for streaming
    const botMsgId = Date.now() + 1;
    setMessages(prev => [...prev, {
      id: botMsgId,
      text: '',
      sender: 'bot',
      timestamp: new Date(),
    }]);

    try {
      const context = getAIBrain();

      // Streaming callback — updates the bot message in-place as chunks arrive
      const onChunk = (_chunk, fullText) => {
        setMessages(prev => prev.map(m =>
          m.id === botMsgId ? { ...m, text: fullText } : m
        ));
      };

      await openaiService.sendMessage(
        userMsgText,
        context,
        'visitor@rasachat.app',
        null,
        newHistory,
        onChunk
      );

    } catch (error) {
      console.error('Chat Error:', error);
      setMessages(prev => prev.map(m =>
        m.id === botMsgId
          ? { ...m, text: "I'm having trouble connecting. Please call us at (770) 643-8812!" }
          : m
      ));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const containerClass = isEmbed ? 'w-full h-full flex flex-col bg-white border-0' : 
    `fixed z-[999999] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col w-[calc(100%-20px)] h-[60vh] bottom-[80px] right-2.5 left-2.5 mx-auto md:w-[360px] md:h-[500px] md:bottom-[100px] md:right-5 md:left-auto md:mx-0`;

  return (
    <motion.div 
      initial={!isEmbed ? { opacity: 0, y: 50 } : false} 
      animate={{ opacity: 1, y: 0 }} 
      className={containerClass}
    >
      <div className={`bg-[#ea580c] text-white p-4 flex justify-between items-center ${isEmbed ? '' : 'rounded-t-xl'}`}>
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
            <SafeIcon icon={FaUserCircle} className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Rasa Sayang Help</h3>
            <p className="text-[10px] opacity-90">Online Assistant</p>
          </div>
        </div>
        {!isEmbed && (
          <button onClick={onClose} className="hover:bg-white hover:bg-opacity-20 rounded-full p-1">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-white">
        {messages.map((message) => (
          <div key={message.id} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`flex items-start space-x-2 max-w-[90%] ${message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${message.sender === 'user' ? 'bg-blue-500' : 'bg-[#ffedd5]'}`}>
                <SafeIcon icon={message.sender === 'user' ? FiUser : FaUserCircle} className={`w-4 h-4 ${message.sender === 'user' ? 'text-white' : 'text-[#ea580c]'}`} />
              </div>
              <div className={`rounded-2xl p-3 shadow-sm ${message.sender === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-800'}`}>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
              </div>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-2xl p-3 animate-pulse text-xs text-gray-400">AI is thinking...</div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-3 border-t border-gray-100 bg-white">
        <div className="flex space-x-2">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1 border border-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-1 focus:ring-[#ea580c] text-sm"
          />
          <button 
            onClick={handleSendMessage} 
            disabled={!inputMessage.trim() || isLoading}
            className="bg-[#ea580c] text-white rounded-full p-2 disabled:opacity-50"
          >
            <SafeIcon icon={FiSend} className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ChatInterface;